import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { applyPageMetadata, applyStructuredData } from '../utils/seo';

export default function BartenderJobsYoungstownPage() {
  useEffect(() => {
    const cleanupMeta = applyPageMetadata({
      title: 'Bartender Jobs in Youngstown, Ohio | BarsConnect — Find Bar Work Fast',
      description: 'Find bartender jobs in Youngstown, Ohio. Full-time, part-time, and event gigs across downtown, Wick Park, and the South Side. Connect directly with bar owners on BarsConnect.',
      keywords: 'bartender jobs youngstown ohio, youngstown bartender jobs, bar jobs youngstown, nightlife jobs ohio, bartending shifts youngstown',
      canonicalUrl: 'https://barsconnect.com/bartender-jobs-youngstown-oh',
      ogTitle: 'Bartender Jobs in Youngstown, Ohio | BarsConnect',
      ogDescription: 'Explore active bartender jobs in Youngstown and connect directly with bars hiring now.',
      twitterTitle: 'Bartender Jobs in Youngstown, Ohio | BarsConnect',
      twitterDescription: 'Find local bartender shifts, part-time roles, and event gigs in Youngstown.',
      robots: 'index,follow',
    });

    const cleanupSchema = applyStructuredData([
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://barsconnect.com/' },
          { '@type': 'ListItem', position: 2, name: 'Bartender Jobs', item: 'https://barsconnect.com/bartender-jobs' },
          { '@type': 'ListItem', position: 3, name: 'Youngstown, Ohio', item: 'https://barsconnect.com/bartender-jobs-youngstown-oh' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        title: 'Bartender Jobs in Youngstown, Ohio',
        description: 'Flexible bartender shifts, event gigs, and local bar opportunities in Youngstown, Ohio.',
        employmentType: 'CONTRACTOR',
        hiringOrganization: {
          '@type': 'Organization',
          name: 'BarsConnect Partner Bars',
          sameAs: 'https://barsconnect.com',
        },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Youngstown',
            addressRegion: 'OH',
            addressCountry: 'US',
          },
        },
      },
    ]);

    return () => {
      cleanupMeta();
      cleanupSchema();
    };
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="youngstown-public-landing-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <header className="mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="youngstown-page-title">
            Bartender Jobs in Youngstown, Ohio
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed" data-testid="youngstown-page-intro">
            Youngstown has always known the bar business. From long-shift regulars to packed weekend crowds, bartending here is fast, social,
            and built on reputation. If you&apos;re searching for bartender jobs in Youngstown, Ohio, this page gives you a direct path to active,
            local opportunities.
          </p>
        </header>

        <section className="mb-8" data-testid="youngstown-scene-section">
          <h2 className="text-2xl font-semibold text-white mb-3" data-testid="youngstown-scene-heading">The Youngstown Bartending Scene</h2>
          <p className="text-muted-foreground leading-relaxed" data-testid="youngstown-scene-copy-1">
            Youngstown&apos;s nightlife and hospitality scene is expanding across downtown and nearby neighborhoods.
            Bars, restaurants, event venues, and private hosts regularly need dependable bartenders for both recurring and last-minute shifts.
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3" data-testid="youngstown-scene-copy-2">
            Typical bartender jobs in Youngstown include evening service, event coverage, and weekend rush shifts.
            Pay rates vary by venue and tips, but experienced bartenders can earn strong nightly totals in high-volume spots.
          </p>
        </section>

        <section className="mb-8" data-testid="youngstown-areas-section">
          <h2 className="text-2xl font-semibold text-white mb-3" data-testid="youngstown-areas-heading">Best Areas to Find Bartending Work</h2>
          <ul className="space-y-2 text-muted-foreground">
            <li data-testid="youngstown-area-downtown"><strong className="text-white">Downtown Youngstown</strong> — Federal Street bars, event venues, and high-traffic weekend service.</li>
            <li data-testid="youngstown-area-wick-park"><strong className="text-white">Wick Park Area</strong> — cocktail-focused spots and community events.</li>
            <li data-testid="youngstown-area-south-boardman"><strong className="text-white">South Side &amp; Boardman</strong> — consistent neighborhood bar and restaurant shifts.</li>
            <li data-testid="youngstown-area-north-liberty"><strong className="text-white">North Side &amp; Liberty</strong> — private events, banquet service, and flexible gig work.</li>
          </ul>
        </section>

        <section className="mb-8" data-testid="youngstown-job-types-section">
          <h2 className="text-2xl font-semibold text-white mb-3" data-testid="youngstown-job-types-heading">Types of Bartending Jobs Available</h2>
          <ul className="space-y-2 text-muted-foreground">
            <li data-testid="youngstown-job-type-full-time"><strong className="text-white">Full-time bartending positions</strong> — stable schedules and regular income.</li>
            <li data-testid="youngstown-job-type-part-time"><strong className="text-white">Part-time bartender shifts</strong> — weekends, nights, and flexible side work.</li>
            <li data-testid="youngstown-job-type-event"><strong className="text-white">Event bartending jobs</strong> — weddings, concerts, corporate events, and private parties.</li>
            <li data-testid="youngstown-job-type-last-minute"><strong className="text-white">Last-minute shift coverage</strong> — urgent openings where bars need help quickly.</li>
          </ul>
        </section>

        <section className="mb-8" data-testid="youngstown-why-barsconnect-section">
          <h2 className="text-2xl font-semibold text-white mb-3" data-testid="youngstown-why-heading">Why BarsConnect Is Different</h2>
          <p className="text-muted-foreground leading-relaxed" data-testid="youngstown-why-copy-1">
            BarsConnect removes the middle layer. Bartenders and bar owners connect directly, message quickly, and move from profile to shift without recruiter delays.
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3" data-testid="youngstown-why-copy-2">
            For bartenders, that means clearer pay expectations and faster replies. For owners, it means filling open shifts with local talent who can actually show up.
          </p>
        </section>

        <Card className="bg-white/5 border border-white/10" data-testid="youngstown-cta-card">
          <CardContent className="p-6 sm:p-8">
            <h2 className="text-2xl font-semibold text-white mb-3" data-testid="youngstown-cta-heading">Ready to Pour in Youngstown?</h2>
            <p className="text-muted-foreground mb-4" data-testid="youngstown-cta-bartender-copy">
              <strong className="text-white">Bartenders:</strong> Browse bartender jobs and connect directly with bars hiring now.
            </p>
            <p className="text-muted-foreground mb-6" data-testid="youngstown-cta-owner-copy">
              <strong className="text-white">Bar Owners:</strong> Post shifts and hire bartenders quickly and directly.
              <br />
              Youngstown&apos;s bar scene is growing. Get in on it.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild data-testid="youngstown-cta-bartenders-btn">
                <Link to="/register">I&apos;m a Bartender</Link>
              </Button>
              <Button asChild variant="outline" data-testid="youngstown-cta-owners-btn">
                <Link to="/register">I&apos;m a Bar Owner</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
